import styled from "styled-components";
import PreviewBranch from "features/owner/components/home/PreviewBranch";
import PreviewNotice from "features/owner/components/home/PreviewNotice";
import { useNavigate } from "react-router-dom";
import useHeader from "shared/hooks/useHeader";
import ICON_BRANCH from "assets/images/icon_branch.png";
import ICON_HOMEWORK from "assets/images/icon_homework.png";
import ICON_NOTICE from "assets/images/icon_notice.png";
import ICON_STATS from "assets/images/icon_stats.png";
import NEXT_SVG from "assets/images/next.svg";
import { hw, scrollbar } from "features/homework/homeworkUi";

const menus = [
    { title: "지점관리", desc: "지점을 등록하고 정보를 수정합니다", to: "/owner/branch", icon: ICON_BRANCH },
    { title: "과제관리", desc: "과제를 등록하고 지점에 배포합니다", to: "/owner/report", icon: ICON_HOMEWORK },
    { title: "공지사항", desc: "공지를 작성하고 대상에게 전달합니다", to: "/owner/notice", icon: ICON_NOTICE },
    { title: "통계", desc: "지점별 원생·과제·미납 현황을 봅니다", to: "/owner/stats", icon: ICON_STATS },
];

const Home = () => {
    const navigate = useNavigate();
    const { name } = useHeader();

    return (
        <Page>
            <PageHeader>
                <Title>{name ? `${name}님, 안녕하세요` : "안녕하세요"}</Title>
                <Desc>지점·과제·공지·통계를 한곳에서 바로 시작하세요.</Desc>
            </PageHeader>
            <Grid>
                {menus.map((item) => (
                    <Tile key={item.title} type="button" onClick={() => navigate(item.to)}>
                        <IconWrap>
                            <img src={item.icon} alt="" />
                        </IconWrap>
                        <TileText>
                            <TileTitle>{item.title}</TileTitle>
                            <TileDesc>{item.desc}</TileDesc>
                        </TileText>
                        <Arrow src={NEXT_SVG} alt="" />
                    </Tile>
                ))}
            </Grid>
            <Panels>
                <Panel>
                    <PanelHead>
                        <div>
                            <PanelTitle>지점</PanelTitle>
                            <PanelDesc>지점을 검색하거나 선택하면 상세 화면으로 이동합니다.</PanelDesc>
                        </div>
                        <MoreButton type="button" onClick={() => navigate("/owner/branch")}>
                            전체보기
                            <img src={NEXT_SVG} alt="" />
                        </MoreButton>
                    </PanelHead>
                    <PanelBody>
                        <PreviewBranch />
                    </PanelBody>
                </Panel>
                <Panel>
                    <PanelHead>
                        <div>
                            <PanelTitle>공지사항</PanelTitle>
                            <PanelDesc>최근 공지를 확인하고 전체 목록으로 이동할 수 있습니다.</PanelDesc>
                        </div>
                        <MoreButton type="button" onClick={() => navigate("/owner/notice")}>
                            전체보기
                            <img src={NEXT_SVG} alt="" />
                        </MoreButton>
                    </PanelHead>
                    <PanelBody>
                        <PreviewNotice teacher={false} />
                    </PanelBody>
                </Panel>
            </Panels>
        </Page>
    );
};

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 20px 24px;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px 16px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${hw.text};
`;

const Desc = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${hw.textMuted};
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    flex-shrink: 0;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Tile = styled.button`
    appearance: none;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 18px;
    min-height: 88px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
    cursor: pointer;
    min-width: 0;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${hw.brandMuted};
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    }

    &:focus-visible {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const IconWrap = styled.span`
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: transparent;

    img {
        width: 52px;
        height: 52px;
        object-fit: contain;
    }
`;

const TileText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
`;

const Arrow = styled.img`
    width: 16px;
    height: 16px;
    opacity: 0.35;
    flex-shrink: 0;
    ${Tile}:hover & {
        opacity: 1;
    }
`;

const TileTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
    letter-spacing: -0.02em;
`;

const TileDesc = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Panels = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 16px;
    flex: 1;
    min-height: 0;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
        overflow: auto;
        ${scrollbar}
    }
`;

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    height: 100%;
    padding: 18px 20px 12px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const PanelHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const PanelTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
    letter-spacing: -0.02em;
`;

const PanelDesc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const PanelBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-top: 12px;
    ${scrollbar}
`;

const MoreButton = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    color: ${hw.brand};
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 4px 0;
    flex-shrink: 0;

    img {
        width: 14px;
        height: 14px;
    }

    &:hover {
        text-decoration: underline;
    }
`;

export default Home;
